import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faEdit,
  faCalendarAlt,
  faRupeeSign,
  faClock,
  faLayerGroup,
  faChevronRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  InputGroup,
  Form,
  Button,
  Modal,
  Badge,
} from "@themesberg/react-bootstrap";

import {
  adminGetCommissionConfig, // current month/single current config
  adminGetAllCommissionConfig, // all commission configs
  adminAddCommissionConfig,
  adminEditCommissionConfig,
} from "../../../pages/api";
import { Link } from "react-router-dom";
import { Routes } from "../../../routes";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const monthOptions = [
  { value: "", label: "Select Month" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const getMonthLabel = (month) => {
  const item = monthOptions.find((m) => String(m.value) === String(month));
  return item?.label || "N/A" || "-";
};

const formatDateTimeToMinutes = (value) => {
  if (!value) return "N/A" || "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const formatAmount = (value) => {
  const num = Number(value || 0);
  return Number.isInteger(num) ? `${num}` : `${num.toFixed(2)}`;
};

const mapCommissionItem = (item) => ({
  id: item?.id ?? Date.now(),
  month: String(item?.month || ""),
  year: String(item?.year || ""),
  total_pool_amount: String(
    item?.total_pool_amount ??
    item?.totalPoolAmount ??
    ""
  ),
  created_at: item?.created_at || "",
  updated_at: item?.updated_at || "",
});

const getCycleSortValue = (item) => {
  const year = Number(item?.year || 0);
  const month = Number(item?.month || 0);
  return year * 12 + month;
};

const sortCommissionConfigs = (list, currentId) => {
  return [...list].sort((a, b) => {
    if (String(a.id) === String(currentId)) return -1;
    if (String(b.id) === String(currentId)) return 1;

    const aValue = getCycleSortValue(a);
    const bValue = getCycleSortValue(b);

    if (bValue !== aValue) return bValue - aValue;

    const aCreated = new Date(a.created_at || 0).getTime();
    const bCreated = new Date(b.created_at || 0).getTime();
    return bCreated - aCreated;
  });
};

export default function AdminCommissionConfig() {
  const [search, setSearch] = useState("");
  const [configs, setConfigs] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [errors, setErrors] = useState({});
  const [currentCycleWarning, setCurrentCycleWarning] = useState("");
  const [formData, setFormData] = useState({
    month: "",
    year: "",
    total_pool_amount: "",
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  // const fetchCurrentCommissionConfig = async () => {
  //   try {
  //     setLoadingCurrent(true);

  //     const response = await adminGetCommissionConfig();
  //     const responseData = response?.data || {};
  //     const apiData = responseData?.data || null;

  //     setCurrentConfig(apiData ? mapCommissionItem(apiData) : null);
  //   } catch (error) {
  //     setCurrentConfig(null);
  //     toast.error(
  //       error?.response?.data?.message || "Unable to fetch current commission."
  //     );
  //   } finally {
  //     setLoadingCurrent(false);
  //   }
  // };


  // update fetchCurrentCommissionConfig
  const fetchCurrentCommissionConfig = async () => {
    try {
      setLoadingCurrent(true);
      setCurrentCycleWarning("");

      const response = await adminGetCommissionConfig();
      const responseData = response?.data || {};
      const apiData = responseData?.data || null;

      setCurrentConfig(apiData ? mapCommissionItem(apiData) : null);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Unable to fetch current commission.";

      setCurrentConfig(null);

      if (message.includes("No commission set for this month") && error?.response?.status === 404) {
        setCurrentCycleWarning("No commission is set for this month. Please set it.");
      } else {
        toast.error(message);
      }
    } finally {
      setLoadingCurrent(false);
    }
  };

  const fetchCommissionConfigs = async (
    page = 1,
    perPage = 10,
    searchTerm = ""
  ) => {
    try {
      setLoading(true);

      const params = {
        page,
        per_page: perPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await adminGetAllCommissionConfig(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};

      const apiConfigs = Array.isArray(apiData)
        ? apiData
        : Array.isArray(apiData?.configs)
          ? apiData.configs
          : [];

      const apiPagination = Array.isArray(apiData)
        ? {}
        : apiData?.pagination || {};

      const mappedList = apiConfigs.map(mapCommissionItem);

      setConfigs(mappedList);

      setPagination({
        current_page: apiPagination.current_page ?? page,
        last_page:
          apiPagination.last_page ??
          Math.max(1, Math.ceil(apiConfigs.length / perPage)),
        per_page: apiPagination.per_page ?? perPage,
        total: apiPagination.total ?? apiConfigs.length,
      });
    } catch (error) {
      // toast.error(
      //   error?.response?.data?.message || "Unable to fetch commission list."
      // );
      setConfigs([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentCommissionConfig();
    fetchCommissionConfigs(1, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchCommissionConfigs(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const sortedConfigs = useMemo(() => {
    const currentId = currentConfig?.id;
    return sortCommissionConfigs(configs, currentId);
  }, [configs, currentConfig]);

  const filteredData = useMemo(() => {
    return sortedConfigs;
  }, [sortedConfigs]);

  const summary = useMemo(() => {
    const current = currentConfig || sortedConfigs[0] || null;

    return {
      month: current?.month || "",
      year: current?.year || "",
      totalPoolAmount: current?.total_pool_amount || "",
      lastUpdated: formatDateTimeToMinutes(
        current?.updated_at || current?.created_at
      ),
    };
  }, [currentConfig, sortedConfigs]);

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Start pages
    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    // End pages
    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    // Middle pages: 1 ... previous current next ... last
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  const openAddModal = () => {
    setEditingConfig(null);
    setErrors({});
    setFormData({
      month: "",
      year: "",
      total_pool_amount: "",
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingConfig(item);
    setErrors({});
    setFormData({
      month: String(item?.month || ""),
      year: String(item?.year || ""),
      total_pool_amount: String(item?.total_pool_amount || ""),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setErrors({});
    setSubmitting(false);
    setFormData({
      month: "",
      year: "",
      total_pool_amount: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "month") {
      if (value !== "" && !/^\d{0,2}$/.test(value)) return;
    }

    if (name === "year") {
      if (value !== "" && !/^\d{0,4}$/.test(value)) return;
    }

    if (name === "total_pool_amount") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      form: "",
    }));
  };

  const validate = () => {
    const nextErrors = {};

    const monthValue = Number(formData.month);
    const yearValue = String(formData.year || "");
    const poolValue = Number(formData.total_pool_amount);

    if (!formData.month) {
      nextErrors.month = "Month is required.";
    } else if (monthValue < 1 || monthValue > 12) {
      nextErrors.month = "Month must be between 1 and 12.";
    }

    if (!formData.year) {
      nextErrors.year = "Year is required.";
    } else if (!/^\d{4}$/.test(yearValue)) {
      nextErrors.year = "Year must be 4 digits.";
    }

    if (!formData.total_pool_amount) {
      nextErrors.total_pool_amount = "Total pool amount is required.";
    } else if (poolValue <= 0) {
      nextErrors.total_pool_amount =
        "Total pool amount must be greater than zero.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        month: Number(formData.month),
        year: Number(formData.year),
        total_pool_amount: Number(formData.total_pool_amount),
      };

      if (editingConfig?.id) {
        const response = await adminEditCommissionConfig(
          editingConfig.id,
          payload
        );
        toast.success(
          response?.data?.message || "Commission updated successfully."
        );
      } else {
        const response = await adminAddCommissionConfig(payload);
        toast.success(
          response?.data?.message || "Commission added successfully."
        );
      }

      closeModal();
      await fetchCurrentCommissionConfig();
      // await fetchCommissionConfigs();
      await fetchCommissionConfigs(
        pagination.current_page,
        pagination.per_page,
        search.trim()
      );
    } catch (error) {
      console.error("Commission save failed:", error);
      toast.error(error?.response?.data?.message || "Unable to save commission.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="commission-header py-4">
        <div className="commission-header-title">
          {/* <h5 className="mb-0">Commission List</h5> */}
          <h2 className="h4 mb-1">Commission List</h2>
        </div>

        <div className="commission-header-actions">
          <InputGroup className="commission-search">
            <InputGroup.Text className="bg-white">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </InputGroup>

          <Button
            type="button"
            onClick={openAddModal}
            className="commission-add-btn fw-semibold"
            style={ACTIVE_BUTTON_STYLE}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Commission
          </Button>
        </div>
      </div>

      {currentCycleWarning ? (
        <Col xs={12}>
          <div
            className="mb-3 px-3 py-2 rounded-3 border"
            style={{
              background: "#f8d7da",
              borderColor: "#f5c2c7",
              color: "#000000",
            }}
          >
            {/* <strong>Warning:</strong> {currentCycleWarning} */}
            {/* <strong>Warning:</strong>  */}
            {currentCycleWarning}
          </div>
        </Col>
      ) : null}

      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Current Cycle</div>
                  <h3 className="mb-0">
                    {summary.month && summary.year
                      ? `${getMonthLabel(summary.month)} ${summary.year}`
                      : "N/A" || "-"}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#005dfb",
                  }}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Pool Amount</div>
                  <h3 className="mb-0">
                    {summary.totalPoolAmount ? formatAmount(summary.totalPoolAmount) : "N/A" || "-"}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#005dfb",
                  }}
                >
                  <FontAwesomeIcon icon={faRupeeSign} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Created</div>
                  <h3 className="mb-0">{summary.lastUpdated || "N/A" || "-"}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#005dfb",
                  }}
                >
                  <FontAwesomeIcon icon={faClock} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="commission-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Commission List</h5>
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr No</th>
                  <th>Month</th>
                  <th>Year</th>
                  <th className="text-center">Total Pool Amount</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading || loadingCurrent ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-5">
                      Loading commission...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{start + index}</td>
                      <td className="fw-semibold">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="me-2 text-muted"
                        />
                        {getMonthLabel(item.month)}
                      </td>
                      <td>{item.year}</td>
                      <td className="fw-semibold text-center">
                        {/* <Badge bg="info" className="px-3 py-2 rounded-pill"> */}
                        {formatAmount(item.total_pool_amount)}
                        {/* </Badge> */}
                      </td>
                      <td className="text-center">
                        <div className="d-inline-flex align-items-center gap-2">
                          <Button
                            type="button"
                            variant="link"
                            className="p-0 text-decoration-none"
                            onClick={() => openEditModal(item)}
                          >
                            <FontAwesomeIcon icon={faEdit} size="sm" />
                          </Button>

                          <Button
                            as={Link}
                            to={Routes.AdminLevelCommissions.path.replace(
                              ":configId",
                              item.id
                            )}
                            type="button"
                            variant="link"
                            className="p-0 text-decoration-none"
                          >
                            <FontAwesomeIcon icon={faLayerGroup} size="sm" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-5">
                      No commission found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() =>
                  fetchCommissionConfigs(
                    Math.max(1, pagination.current_page - 1),
                    pagination.per_page,
                    search.trim()
                  )
                }
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() =>
                      fetchCommissionConfigs(item, pagination.per_page, search.trim())
                    }
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""
                      }`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() =>
                  fetchCommissionConfigs(
                    Math.min(pagination.last_page, pagination.current_page + 1),
                    pagination.per_page,
                    search.trim()
                  )
                }
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered backdrop="static">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingConfig ? "Edit Commission" : "Add Commission"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Month <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="month"
                    value={formData.month}
                    onChange={handleChange}
                    isInvalid={!!errors.month}
                    style={{ boxShadow: "none" }}
                  >
                    {monthOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.month}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Year <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="year"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="2026"
                    value={formData.year}
                    onChange={handleChange}
                    isInvalid={!!errors.year}
                    style={{ boxShadow: "none" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.year}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Pool Amount <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="total_pool_amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="4000"
                    value={formData.total_pool_amount}
                    onChange={handleChange}
                    isInvalid={!!errors.total_pool_amount}
                    style={{ boxShadow: "none" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.total_pool_amount}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              style={ACTIVE_BUTTON_STYLE}
              disabled={submitting}
            >
              {submitting
                ? "Saving..."
                : editingConfig
                  ? "Update Commission"
                  : "Add Commission"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        .commission-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .commission-header-title {
          flex: 0 0 auto;
        }

        .commission-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex: 1;
        }

        .commission-search {
          width: 100%;
          max-width: 300px;
          flex: 0 1 360px;
        }

        .commission-add-btn {
          white-space: nowrap;
          flex: 0 0 auto;
          padding: 10px 20px;
        }

        .modal-content {
          border-radius: 18px;
        }

        @media (max-width: 767.98px) {
          .commission-header {
            flex-direction: column;
            align-items: stretch;
          }

          .commission-header-title {
            width: 100%;
          }

          .commission-header-actions {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }

          .commission-search {
            width: 100%;
            max-width: none;
            flex: 1 1 auto;
          }

          .commission-add-btn {
            width: 100%;
          }

          .commission-list-top {
            display: flex;
            flex-direction: row !important;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: nowrap;
          }
        }
      `}</style>
    </>
  );
}